import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Loader2, Shield, Filter, ChevronLeft, ChevronRight, UserCheck, FileText, CalendarDays, Settings, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORY_META = {
  leave: { label: 'Leave', icon: CalendarDays, color: 'bg-emerald-100 text-emerald-700' },
  payroll: { label: 'Payroll', icon: FileText, color: 'bg-blue-100 text-blue-700' },
  general: { label: 'General', icon: Settings, color: 'bg-slate-100 text-slate-600' },
};

const ACTION_LABELS = {
  leave_applied: 'Leave Applied',
  leave_manager_approved: 'Manager Approved',
  leave_hr_approved: 'HR Approved',
  leave_rejected: 'Leave Rejected',
  leave_cancelled: 'Leave Cancelled',
  org_chart_updated: 'Org Chart Updated',
  payroll_run: 'Payroll Run',
  letter_generated: 'Letter Generated',
};

export default function AuditLogsPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(0);
  const perPage = 25;

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: perPage, skip: page * perPage });
      if (category !== 'all') params.append('category', category);
      const { data } = await api.get(`/api/audit-logs?${params}`);
      setLogs(data.logs);
      setTotal(data.total);
    } catch (e) { toast.error('Failed to load audit logs'); }
    setLoading(false);
  }, [category, page]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const formatTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div data-testid="audit-logs-page">
      <Badge className="bg-[#0D2850] text-white text-[10px] mb-3 hover:bg-[#0D2850]">// AUDIT LOGS</Badge>
      <h1 className="text-2xl font-semibold text-[#0D2850] tracking-tight">Audit Logs</h1>
      <p className="text-sm text-slate-500 mt-1 mb-6">Track all critical actions across the system</p>

      <div className="flex items-center gap-3 mb-4">
        <Filter className="w-4 h-4 text-slate-400" />
        <Select value={category} onValueChange={v => { setCategory(v); setPage(0); }}>
          <SelectTrigger className="w-[180px] h-9" data-testid="audit-category-filter">
            <SelectValue placeholder="Filter by category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            <SelectItem value="leave">Leave</SelectItem>
            <SelectItem value="payroll">Payroll</SelectItem>
            <SelectItem value="general">General</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-400 ml-auto">{total} total entries</span>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-[#0D2850]" /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16">
              <Shield className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No audit logs found</p>
            </div>
          ) : (
            <ScrollArea className="max-h-[calc(100vh-320px)]">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Actor</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead>Category</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map(log => {
                    const catMeta = CATEGORY_META[log.category] || CATEGORY_META.general;
                    return (
                      <TableRow key={log.id} data-testid={`audit-log-${log.id}`}>
                        <TableCell className="text-xs text-slate-500 font-tabular">
                          <div className="flex items-center gap-1.5">
                            <Clock className="w-3 h-3" />
                            {formatTime(log.timestamp)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-[#0D2850]">
                            {ACTION_LABELS[log.action] || log.action}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm">{log.actor_name}</TableCell>
                        <TableCell className="text-sm text-slate-600">{log.target || '-'}</TableCell>
                        <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{log.details || '-'}</TableCell>
                        <TableCell>
                          <Badge className={`text-[9px] ${catMeta.color} hover:opacity-90`}>{catMeta.label}</Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <Button size="sm" variant="outline" className="h-8" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="audit-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-slate-500">Page {page + 1} of {totalPages}</span>
          <Button size="sm" variant="outline" className="h-8" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="audit-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
